const values = [
  {
    icon: "ri-git-merge-line",
    title: "Reduce Operational Friction",
    desc: "Eliminate manual coordination gaps across teams and departments—so nothing slips through.",
  },
  {
    icon: "ri-flow-chart",
    title: "Automate Critical Workflows",
    desc: "From intake to execution, every process runs seamlessly end-to-end without manual effort.",
  },
  {
    icon: "ri-eye-line",
    title: "Centralized Visibility",
    desc: "Real-time clarity across projects, cases, and patient workflows in one unified view.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Execution Reliability",
    desc: "Ensure nothing is missed, delayed, or lost across your operations. Every time.",
  },
];

export default function ValueProposition() {
  return (
    <section
      id="services"
      className="bg-white py-28 px-6 md:px-10"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-16">
          <p className="text-xs font-medium text-[#4F46E5] uppercase tracking-widest mb-4">
            Why Noveka AI
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1F2933] tracking-[-0.03em] leading-[1.08]">
            Operational Intelligence.
            <br />
            <span className="text-[#6B7280] font-normal">Not Just Automation.</span>
          </h2>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* Large feature card — left */}
          <div className="lg:col-span-7 rounded-[20px] border border-[#E5E7EB] overflow-hidden group hover:shadow-[0_8px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300">
            {/* Image */}
            <div className="w-full h-56 overflow-hidden bg-[#F7F8FA]">
              <img
                src="https://public.readdy.ai/ai/img_res/66d4eb73d2650915c10b73dac4ad45bd.jpg"
                alt="Intelligent workflow system"
                className="w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-500"
              />
            </div>
            {/* Content */}
            <div className="p-8 bg-white">
              <div className="w-10 h-10 flex items-center justify-center rounded-[10px] bg-[#EEF2FF] border border-[#C7D2FE] mb-5">
                <i className="ri-git-merge-line text-xl text-[#4F46E5]"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#1F2933] mb-2 tracking-[-0.01em]">
                {values[0].title}
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed max-w-md">{values[0].desc}</p>
            </div>
          </div>

          {/* Right column — two stacked cards */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="flex-1 rounded-[20px] border border-[#E5E7EB] bg-[#F7F8FA] p-8 group hover:shadow-[0_6px_28px_rgba(0,0,0,0.07)] hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 flex items-center justify-center rounded-[10px] bg-white border border-[#E5E7EB] mb-5 group-hover:bg-[#EEF2FF] group-hover:border-[#C7D2FE] transition-colors duration-200">
                <i className="ri-flow-chart text-xl text-[#4F46E5]"></i>
              </div>
              <h3 className="text-lg font-semibold text-[#1F2933] mb-2 tracking-[-0.01em]">{values[1].title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">{values[1].desc}</p>
            </div>

            <div className="flex-1 rounded-[20px] border border-[#E5E7EB] bg-[#4F46E5] p-8 group hover:bg-[#4338CA] hover:shadow-[0_8px_32px_rgba(79,70,229,0.3)] hover:-translate-y-1 transition-all duration-300">
              <div className="w-10 h-10 flex items-center justify-center rounded-[10px] bg-white/20 border border-white/20 mb-5">
                <i className="ri-eye-line text-xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2 tracking-[-0.01em]">{values[2].title}</h3>
              <p className="text-sm text-indigo-200 leading-relaxed">{values[2].desc}</p>
            </div>
          </div>

          {/* Bottom wide card */}
          <div className="lg:col-span-12 rounded-[20px] border border-[#E5E7EB] overflow-hidden group hover:shadow-[0_8px_40px_rgba(0,0,0,0.07)] hover:-translate-y-1 transition-all duration-300">
            <div className="flex flex-col lg:flex-row">
              <div className="p-8 lg:p-10 flex-1">
                <div className="w-10 h-10 flex items-center justify-center rounded-[10px] bg-[#EEF2FF] border border-[#C7D2FE] mb-6">
                  <i className="ri-shield-check-line text-xl text-[#4F46E5]"></i>
                </div>
                <h3 className="text-2xl font-semibold text-[#1F2933] mb-3 tracking-[-0.02em]">{values[3].title}</h3>
                <p className="text-sm text-[#6B7280] leading-relaxed max-w-sm">{values[3].desc}</p>

                <div className="mt-8 flex flex-wrap gap-3">
                  {["Zero missed tasks", "Full audit trail", "SLA compliance", "Real-time alerts"].map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F7F8FA] border border-[#E5E7EB] text-xs font-medium text-[#374151]"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#4F46E5]"></span>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:w-80 h-48 lg:h-auto bg-[#F7F8FA] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=clean%20minimal%20abstract%20geometric%20pattern%20representing%20reliability%20and%20precision%20soft%20grid%20lines%20on%20light%20gray%20white%20background%20professional%20systematic%20organization%20with%20ordered%20structure%20elegant%20minimal%20design%20no%20text&width=480&height=280&seq=noveka-vp-reliability-1&orientation=landscape"
                  alt="Execution reliability"
                  className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
